import { DOCUMENT, inject, Injectable } from '@angular/core';
import { AzureLoginResult, loginAborted, loginRedirect, LoginResult, resetPasswordRequested } from '@libs/shared/models';
import { catchError, defer, map, Observable, of, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { createUserToken } from './createUserToken';
import { OAuthService } from './oauth';

@Injectable({ providedIn: 'root' })
export class LoginService {
  private readonly oauthService: OAuthService = inject(OAuthService);
  private readonly document: Document = inject(DOCUMENT);

  public login(): Observable<LoginResult | undefined> {
    return defer(() => {
      return this.oauthService.login().pipe(
        map(result => this.createLoginResult(result)),
        catchError(error => this.onLoginError(error)),
      );
    });
  }

  public register(): Observable<LoginResult | undefined> {
    return defer(() => {
      return this.oauthService.register().pipe(
        map(result => this.createLoginResult(result)),
        catchError(error => this.onLoginError(error)),
      );
    });
  }

  public logout(): Observable<void> {
    return this.oauthService.logout();
  }

  public refresh(): Observable<LoginResult | undefined> {
    return this.oauthService.refreshToken().pipe(
      map(result => {
        if (!result) {
          return undefined;
        }

        return {
          authResult: result,
          authToken: createUserToken(result),
        };
      }),
    );
  }

  private createLoginResult(result: AzureLoginResult | undefined): LoginResult {
    if (!result) {
      throw new Error('Error while logging in');
    }

    return {
      authResult: result,
      authToken: createUserToken(result),
    };
  }

  private onLoginError(error?: unknown): Observable<undefined> {
    if (error instanceof Error && (error.message === loginRedirect || error.message === resetPasswordRequested)) {
      return of(undefined);
    }

    if (error instanceof Error && error.message === loginAborted) {
      void this.reloadView();

      return of(undefined);
    }

    // if after the azure login something fails we need to log out of azure,
    return this.logout().pipe(
      switchMap(() => throwError(() => error)),
    );
  }

  private reloadView(): void {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    void this.document.defaultView!.location.reload();
  }
}
